/**
 * Type-ahead combobox.
 *
 * Free text is always allowed — the driver must be able to enter an address
 * the app has never seen without fighting a picker. Suggestions accelerate the
 * common case; they never gate it.
 */

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

export interface ComboboxOption<T> {
  key: string;
  value: T;
  label: string;
  detail?: string;
  badge?: ReactNode;
}

export interface ComboboxProps<T> {
  value: string;
  onChange: (text: string) => void;
  onSelect: (option: ComboboxOption<T>) => void;
  options: ReadonlyArray<ComboboxOption<T>>;
  placeholder?: string;
  disabled?: boolean;
  /** Rendered under the list, e.g. a "search online" affordance. */
  footer?: ReactNode;
  id?: string;
  autoFocus?: boolean;
  loading?: boolean;
}

export function Combobox<T>({
  value,
  onChange,
  onSelect,
  options,
  placeholder,
  disabled,
  footer,
  id,
  autoFocus,
  loading,
}: ComboboxProps<T>) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listboxId = `${inputId}-listbox`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const visible = useMemo(() => options.slice(0, 8), [options]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [value]);

  function choose(option: ComboboxOption<T>) {
    onSelect(option);
    setOpen(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, visible.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, -1));
    } else if (event.key === 'Enter') {
      const option = visible[activeIndex];
      if (open && option) {
        event.preventDefault();
        choose(option);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    } else if (event.key === 'Tab') {
      // Tab accepts the highlighted suggestion, which is how a fast typist
      // expects an inline completion to behave.
      const option = visible[activeIndex];
      if (open && option) choose(option);
    }
  }

  const showList = open && (visible.length > 0 || Boolean(footer));

  return (
    <div ref={containerRef} className="relative">
      <input
        id={inputId}
        className="field-input"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // Choosing an option cancels the pointerdown, so the input never loses
        // focus that way and this only fires when the driver has genuinely
        // moved on. Without it, tabbing to the next field leaves two
        // suggestion lists open on top of each other.
        onBlur={() => setOpen(false)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        autoCapitalize="words"
        spellCheck={false}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex >= 0 && visible[activeIndex]
            ? `${listboxId}-${visible[activeIndex]!.key}`
            : undefined
        }
      />

      {loading && (
        <span className="border-line-strong border-t-accent absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2" />
      )}

      {showList && (
        <ul
          id={listboxId}
          role="listbox"
          className="border-line bg-surface absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-xl border shadow-lg"
        >
          {visible.map((option, index) => (
            <li
              key={option.key}
              id={`${listboxId}-${option.key}`}
              role="option"
              aria-selected={index === activeIndex}
            >
              <button
                type="button"
                className={`flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors ${
                  index === activeIndex ? 'bg-accent-soft' : 'hover:bg-subtle'
                }`}
                onPointerDown={(event) => {
                  // Fire before the input's blur so the click is not swallowed.
                  event.preventDefault();
                  choose(option);
                }}
              >
                <span className="min-w-0 flex-1">
                  <span className="text-heading block truncate text-sm">{option.label}</span>
                  {option.detail && (
                    <span className="text-muted block truncate text-xs">{option.detail}</span>
                  )}
                </span>
                {option.badge}
              </button>
            </li>
          ))}
          {footer && <li className="border-line border-t p-2">{footer}</li>}
        </ul>
      )}
    </div>
  );
}
